// Members (Pass Manager) — console-standard rebuild. Dark/light command palette,
// full-bleed, large type, interactive: pass-mix donut, refund panel, sortable/
// searchable/paginated roster with a row-click inspector that deep-links to Users.
// All original queries + status filters preserved.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Table, Drawer, Pill, Empty, Donut, StatLine, ExportButton, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';
import { setFocus } from '../utils/consoleBus';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = n => (n == null ? '…' : Number(n).toLocaleString());
const fmtUSD = n => (n == null ? '…' : '$' + Number(n).toFixed(2));
const timeLabel = ts => (!ts ? '—' : new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }));
const daysUntil = ts => (!ts ? null : Math.ceil((new Date(ts) - Date.now()) / 86400000));

const PASS_PALETTE = { MONTHLY:'#85b7eb', ANNUAL:'#f0b357' };

function usePassData() {
  const [d, setD] = useState({ passes: [], refunds: [], events: [], loading: true });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Global data must come through a SECURITY DEFINER admin RPC — direct
        // user_passes/refund_requests reads are RLS-scoped to the operator's
        // own rows and return near-zero for the real catalogue.
        const { data } = await supabase.rpc('admin_pass_manager');
        if (!cancelled) setD({
          passes: (data && data.passes) || [], refunds: (data && data.refunds) || [],
          events: (data && data.events) || [], loading: false,
        });
      } catch (_e) { if (!cancelled) setD(s => ({ ...s, loading: false })); }
    })();
    return () => { cancelled = true; };
  }, []);
  return d;
}

function statusOf(p, now) {
  if (p.refunded_at) return 'refunded';
  if (p.expires_at <= now) return 'expired';
  const d = daysUntil(p.expires_at);
  if (d != null && d >= 0 && d <= 7) return 'expiring';
  return 'active';
}

function PassInspector({ p, onNav }) {
  const { t } = useConsoleTheme();
  const now = new Date().toISOString();
  const st = statusOf(p, now);
  const d = daysUntil(p.expires_at);
  return (
    <div>
      <div style={{ fontSize:'1.05rem', fontWeight:500, color:t.tx }}>{(p.pass_type || '').toUpperCase() || '—'}</div>
      <div style={{ fontSize:'0.8rem', color:t.mut, fontFamily:'monospace', wordBreak:'break-all', marginBottom:12 }}>{p.user_id || 'anonymous'}</div>
      <div style={{ marginBottom:14 }}>
        <Pill tone={st === 'active' ? 'up' : st === 'expiring' ? 'warn' : st === 'refunded' ? 'danger' : undefined}>
          {st === 'expiring' ? `${d}d left` : st.charAt(0).toUpperCase() + st.slice(1)}
        </Pill>
      </div>
      <StatLine label="Pass type" value={p.pass_type || '—'} />
      <StatLine label="Created" value={timeLabel(p.created_at)} />
      <StatLine label="Expires" value={timeLabel(p.expires_at)} color={st === 'expiring' ? t.warn : t.tx} />
      {d != null && <StatLine label="Days remaining" value={d >= 0 ? `${d}d` : 'expired'} color={d >= 0 && d <= 7 ? t.warn : t.tx} />}
      {p.refunded_at && <StatLine label="Refunded" value={timeLabel(p.refunded_at)} color={t.danger} />}
      <StatLine label="Stripe intent" value={p.stripe_payment_intent_id ? 'on file' : '—'} />
      <div style={{ height:1, background:t.bd, margin:'14px 0' }} />
      {p.user_id && (
        <button onClick={() => { setFocus({ type:'user', value:p.user_id }); onNav && onNav('users'); }}
          style={{ fontSize:'0.85rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>
          Find this user →
        </button>
      )}
    </div>
  );
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const { passes, refunds, events, loading } = usePassData();
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(null);
  const now = new Date().toISOString();

  const active   = passes.filter(p => statusOf(p, now) === 'active' || statusOf(p, now) === 'expiring');
  const expired  = passes.filter(p => statusOf(p, now) === 'expired');
  const refunded = passes.filter(p => p.refunded_at);
  const expiring = passes.filter(p => statusOf(p, now) === 'expiring');
  const totalRevenue = events.reduce((s, e) => s + (parseFloat(e.price_usd) || 0), 0);
  const pendingRefunds = refunds.filter(r => r.status === 'pending');

  const passTypeMap = {};
  passes.forEach(p => { const k = p.pass_type || 'other'; passTypeMap[k] = (passTypeMap[k] || 0) + 1; });
  const typeDonut = Object.keys(passTypeMap).sort((a, b) => passTypeMap[b] - passTypeMap[a])
    .map((k, i) => ({ label: k.toUpperCase(), value: passTypeMap[k], color: PASS_PALETTE[k.toUpperCase()] || PALETTE[i % PALETTE.length] }));

  const filtered = passes.filter(p => filter === 'all' ? true : statusOf(p, now) === filter);

  const cols = [
    { label:'User', mono:true, text:r => r.user_id || 'anonymous', render:r => <span style={{ fontFamily:'monospace', fontSize:'0.85rem' }}>{(r.user_id || 'anonymous').slice(0, 16)}…</span> },
    { label:'Type', text:r => r.pass_type || '', render:r => <b style={{ color: PASS_PALETTE[(r.pass_type || '').toUpperCase()] || t.accent }}>{(r.pass_type || '').toUpperCase() || '—'}</b> },
    { label:'Status', text:r => statusOf(r, now), render:r => {
        const st = statusOf(r, now); const d = daysUntil(r.expires_at);
        return <Pill tone={st === 'active' ? 'up' : st === 'expiring' ? 'warn' : st === 'refunded' ? 'danger' : undefined}>{st === 'expiring' ? `${d}d left` : st.charAt(0).toUpperCase() + st.slice(1)}</Pill>;
      } },
    { label:'Created', muted:true, sort:r => r.created_at ? +new Date(r.created_at) : 0, text:r => timeLabel(r.created_at), render:r => timeLabel(r.created_at) },
    { label:'Expires', align:'right', sort:r => r.expires_at ? +new Date(r.expires_at) : 0, text:r => timeLabel(r.expires_at),
      render:r => { const ex = statusOf(r, now) === 'expiring'; return <span style={{ color: ex ? t.warn : t.mut, fontWeight: ex ? 600 : 400 }}>{timeLabel(r.expires_at)}</span>; } },
  ];

  const chips = [['all','All'],['active','Active'],['expiring','Expiring'],['expired','Expired'],['refunded','Refunded']];
  const chipBar = (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {chips.map(([f, label]) => (
        <button key={f} onClick={() => setFilter(f)}
          style={{ fontSize:'0.85rem', padding:'4px 11px', borderRadius:999, cursor:'pointer', fontFamily:F,
            border:`1px solid ${filter === f ? t.accent : t.bd}`, background: filter === f ? t.accent : t.period,
            color: filter === f ? (t.bg === '#f2f1ef' ? '#fff' : '#06222e') : t.mut, fontWeight: filter === f ? 600 : 500 }}>
          {label}{f === 'expiring' && expiring.length > 0 ? ` · ${expiring.length}` : ''}
        </button>
      ))}
    </div>
  );

  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(4), gap:10 }}>
        <KPI label="Active passes" value={loading ? '…' : fmt(active.length)} sub={`${fmt(expiring.length)} expiring in 7d`} subColor={expiring.length > 0 ? t.warn : t.mut} accent="#5dcaa5" />
        <KPI label="Total issued" value={loading ? '…' : fmt(passes.length)} sub={`${fmt(expired.length)} expired`} accent="#314F5C" />
        <KPI label="Refunded" value={loading ? '…' : fmt(refunded.length)} sub={refunded.length > 0 ? 'see refund panel' : 'none'} subColor={refunded.length > 0 ? t.danger : t.mut} accent={refunded.length > 0 ? '#e8736b' : '#7fa0b4'} />
        <KPI label="Revenue (access)" value={loading ? '…' : '$' + totalRevenue.toFixed(0)} sub={`${fmt(events.length)} purchases`} accent="#6F8FA7" />
      </div>

      <SectionLabel>Mix & refunds</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ Pass mix">
          {loading ? <Empty>Loading…</Empty> : typeDonut.length === 0 ? <Empty>No passes issued yet.</Empty>
            : <Donut data={typeDonut} center={{ value: fmt(passes.length), label: 'passes' }} />}
        </Panel>
        <Panel title="◈ Pending refund requests" right={pendingRefunds.length > 0 ? <Pill tone="warn">{pendingRefunds.length}</Pill> : null}>
          {loading ? <Empty>Loading…</Empty> : pendingRefunds.length === 0 ? <Empty>No pending refund requests — all clear.</Empty>
            : pendingRefunds.slice(0, 6).map(r => (
              <div key={r.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:`1px solid ${t.bd}` }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.95rem', color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.email || 'Anonymous'}</div>
                  <div style={{ fontSize:'0.8rem', color:t.mut, marginTop:1 }}>{r.pass_type} · {fmtUSD(r.price_usd)} · {r.reason || 'no reason'}</div>
                </div>
                <Pill tone="warn">pending</Pill>
                <span style={{ fontSize:'0.8rem', color:t.mut, whiteSpace:'nowrap' }}>{timeLabel(r.created_at)}</span>
              </div>
            ))}
        </Panel>
      </div>

      <SectionLabel right={chipBar}>Pass roster</SectionLabel>
      <Panel title="◷ All passes" right={<ExportButton filename="sanoire-passes" cols={cols} rows={filtered} />}>
        {loading ? <Empty>Loading passes…</Empty>
          : <Table empty="No passes match this filter." cols={cols} rows={filtered} onRowClick={r => setSel(r)} pageSize={25} searchable />}
      </Panel>

      <Drawer open={!!sel} onClose={() => setSel(null)} title="Pass detail">
        {sel && <PassInspector p={sel} onNav={onNav} />}
      </Drawer>
    </div>
  );
}

export default function PassManager(props) {
  return <ConsoleShell title="Members"><Body onNav={props.onNav} /></ConsoleShell>;
}
